import { Button, Card, CardContent, Checkbox, FormControlLabel, Grid, MenuItem, Chip as MuiChip, TablePagination, TextField, Typography } from '@mui/material'
import { GreenText, Title } from 'components'
import { useLayout } from 'context'
import { getOhioBuildABoxes as getOhioBuildABoxesApi } from 'controllers/ohioBuildABoxes/index'
import { OhioBuildABoxDto, OhioBuildABoxQueryParams } from 'dtos/ohioBuildABoxes'
import { DEBOUNCE_DELAY } from 'localConstants'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'
import OhioBuildABoxCard from './OhioBuildABoxCard'

const sessionStorageKey = 'ohioBuildABox:queryParams'

const defaultQueryParams: OhioBuildABoxQueryParams = {
  includeBoxesDelivered: false,
  page: 0,
  pageSize: 10
}

const getQueryParams = (): OhioBuildABoxQueryParams => {
  if (sessionStorage.getItem(sessionStorageKey)) return JSON.parse(sessionStorage.getItem(sessionStorageKey)!)
  return defaultQueryParams
}

export default function OhioBuildABoxes() {
  const { setIsLayoutLoading } = useLayout()
  const navigate = useNavigate()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')

  const [queryParams, setQueryParams] = useState<OhioBuildABoxQueryParams>(getQueryParams())
  const [ohioBuildABoxes, setOhioBuildABoxes] = useState<OhioBuildABoxDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getOhioBuildABoxes = useDebounce((queryParams: OhioBuildABoxQueryParams) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(queryParams))

    setIsLayoutLoading(true)
    getOhioBuildABoxesApi(queryParams)
      .then(res => {
        if ((res.totalCount ?? 0) < queryParams.page * queryParams.pageSize) setQueryParams({ ...queryParams, page: 0 })
        setOhioBuildABoxes(res.value)
        setTotalCount(res.totalCount ?? 0)
      })
      .finally(() => setIsLayoutLoading(false))
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    getOhioBuildABoxes(queryParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams])

  return (
    <>
      <Title>Build A Box (Ohio)</Title>

      <Grid container spacing={1} alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Build A Box
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item container spacing={1} xs={12} sm={true} alignContent='center'>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label='Filter Field'
                      onChange={e => {
                        setFilterField(e.target.value)
                        setFilterValue('')
                      }}
                      select
                      size='small'
                      sx={{ minWidth: 200 }}
                      value={filterField || ''}
                    >
                      <MenuItem value='ohioTitleOwnerName'>Owner</MenuItem>

                      <MenuItem value='last6OfVIN'>Last 6 Of VIN</MenuItem>

                      <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                      <MenuItem value='year'>Year</MenuItem>

                      <MenuItem value='make'>Make</MenuItem>

                      <MenuItem value='model'>Model</MenuItem>
                    </TextField>
                  </Grid>

                  {['', 'ohioTitleOwnerName', 'last6OfVIN', 'importerStockNumber', 'year', 'make', 'model'].includes(filterField) && (
                    <Grid item xs={12} sm={2}>
                      <TextField
                        disabled={!filterField}
                        label='Filter Value'
                        onChange={e => setFilterValue(e.target.value)}
                        size='small'
                        type={filterField === 'year' ? 'number' : 'text'}
                        value={filterValue || ''}
                      />
                    </Grid>
                  )}

                  <Grid item>
                    <Button
                      color='inherit'
                      disabled={!filterField || !filterValue}
                      size='medium'
                      variant='contained'
                      onClick={() => {
                        setQueryParams({ ...queryParams, [filterField]: filterValue })

                        setFilterField('')
                        setFilterValue('')
                      }}
                    >
                      APPLY FILTER
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      color='secondary'
                      disabled={
                        !Boolean(
                          queryParams.includeBoxesDelivered ||
                            queryParams.ohioTitleOwnerName ||
                            queryParams.last6OfVIN ||
                            queryParams.importerStockNumber ||
                            queryParams.year ||
                            queryParams.make ||
                            queryParams.model
                        )
                      }
                      size='medium'
                      variant='text'
                      onClick={() => setQueryParams(defaultQueryParams)}
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>

                  <Grid item>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={queryParams.includeBoxesDelivered}
                          color='primary'
                          onChange={() => setQueryParams({ ...queryParams, includeBoxesDelivered: !queryParams.includeBoxesDelivered })}
                        />
                      }
                      label='Include Boxes Delivered'
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} sm='auto' container alignItems='center'>
                  <Grid item>
                    <Button color='primary' onClick={() => navigate('new')} variant='contained'>
                      ADD BOX
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  {queryParams.ohioTitleOwnerName && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Owner contains "${queryParams.ohioTitleOwnerName}"`}
                        onDelete={() => setQueryParams({ ...queryParams, ohioTitleOwnerName: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.last6OfVIN && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Last 6 Of VIN contains "${queryParams.last6OfVIN}"`}
                        onDelete={() => setQueryParams({ ...queryParams, last6OfVIN: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.importerStockNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                        onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.year && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Year is ${queryParams.year}`}
                        onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.make && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Make contains "${queryParams.make}"`}
                        onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.model && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Model contains "${queryParams.model}"`}
                        onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {ohioBuildABoxes.map(ohioBuildABox => (
          <Grid item xs={12} key={ohioBuildABox.ohioBuildABoxGuid}>
            <OhioBuildABoxCard ohioBuildABox={ohioBuildABox} />
          </Grid>
        ))}

        <Grid item xs={12}>
          <GreenText>
            {`**Build A Box:**

Build A Box lists all boxes of title applications for processing by the Ohio BMV. All vehicles associated with the box are listed on that boxes’ card and are grouped by bundle.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Owner
   - Last 6 Of VIN
   - Importer Stock #
   - Year
   - Make
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Owner: Displays a free-type filter field.
   - Last 6 Of VIN: Displays a free-type filter field.
   - Importer Stock #: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Include Delivered Boxes Check Box:**

Clicking this check box will change the filtering on the screen to also display Boxes that have been delivered

**Add Box Button:**

Clicking the “Add Box” button opens an empty Build A Box – Details page.

**Build A Box Cards:**

Cards on the “Build A Box” screen are sorted by “Box Shipped” date newest to oldest. By default, “Include Box Delivered” filter is unchecked. A box is considered delivered when the “Box Delivered" date field is populated.

**Box Locked Chip:**

This chip is displayed if the box has been marked as locked in the Build A Box – Details page.

**Manage Box Button:**

Clicking “Manage Box” navigates to the Build A Box – Details page for the selected box.

**Owner Column:**

Displays the owner’s name from the “Owner Name” field under Vehicle Details > Titles tab.

**Bundle Locked Chip:**

This chip is displayed if a bundle within the box has been marked as locked in the Build A Box – Details page.

**Last 6 Of VIN Column:**

Displays the last six characters of the vehicle’s VIN.

**Importer Stock #:**

Displays a sequential number starting at 20000, followed by a three-character Exporter Code or Buyer Code (if the vehicle has a buyer).

This field is populated only when the vehicle is locked.

**Year Column:**

Displays the vehicle’s year from Vehicle Details.

**Make Column:**

Displays the vehicle’s make from Vehicle Details.

**Model Column:**

Displays the vehicle’s model from Vehicle Details.

**Vehicle Check In Date:**

Displays the check in date for when the vehicles check in was completed

**Application Packet Ready For Build A Box:**

Displays the "Application Packet Ready For Build A Box" date from Ohio Titles.

**Count:**

Displays the total number of vehicles in the box.

**Tracking #:**

Displays the FedEx tracking number of the box, read from Build A Box – Details.

The tracking number is a clickable link that opens the FedEx website.

**Box Shipped:**

Reads from the FedEx API.

“Box Shipped” means the box has been dropped off at FedEx, and FedEx confirms possession via their API.

**Box Delivered:**

Reads from the FedEx API.

“Box Delivered” means FedEx reports a “delivered” date for the box’s tracking number.

**Created:**

Displays the date and user who created the box.

**Modified:**

Displays the date and user who most recently modified the box.`}
          </GreenText>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => setQueryParams({ ...queryParams, page })}
            onRowsPerPageChange={e => setQueryParams({ ...queryParams, pageSize: e.target.value as unknown as number })}
            page={queryParams.page}
            rowsPerPage={queryParams.pageSize}
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
